import React from "react";
import "./review.tokens.scss";
import "./BookingStepper.scss";

/**
 * Stepper — visual by default; optional interactive mode (parent supplies navigation + disabled rules).
 *
 * @param {{ id: string, label: string }[]} steps
 * @param {string} activeStepId
 * @param {string} [className]
 * @param {boolean} [interactive]
 * @param {(stepId: string) => void} [onStepClick]
 * @param {(stepId: string) => boolean} [getStepDisabled] — return true to disable that step’s control
 * @param {Set<string>|string[]} [completedStepIds] — steps before the active one (visual only)
 */
export default function BookingStepper({
  steps = [],
  activeStepId,
  className = "",
  interactive = false,
  onStepClick,
  getStepDisabled,
  completedStepIds,
}) {
  if (!steps.length) return null;

  const completed =
    completedStepIds instanceof Set
      ? completedStepIds
      : Array.isArray(completedStepIds)
        ? new Set(completedStepIds)
        : new Set();

  return (
    <nav
      className={`review-ds review-booking-stepper${className ? ` ${className}` : ""}`.trim()}
      aria-label="שלבי הזמנה"
    >
      <ol className="review-booking-stepper__list">
        {steps.map((s, i) => {
          const active = activeStepId === s.id;
          const isCompleted = !active && completed.has(s.id);
          const disabled = interactive && (getStepDisabled?.(s.id) ?? false);
          const pillClass =
            "review-booking-stepper__pill" +
            (active ? " review-booking-stepper__pill--active" : "") +
            (isCompleted ? " review-booking-stepper__pill--completed" : "");

          const inner = (
            <>
              <span className="review-booking-stepper__index">{i + 1}</span>
              <span className="review-booking-stepper__label">{s.label}</span>
            </>
          );

          return (
            <li key={s.id} className="review-booking-stepper__item">
              {interactive ? (
                <button
                  type="button"
                  className={pillClass}
                  aria-current={active ? "step" : undefined}
                  disabled={!!disabled}
                  onClick={() => {
                    if (disabled) return;
                    onStepClick?.(s.id);
                  }}
                >
                  {inner}
                </button>
              ) : (
                <span className={pillClass} aria-current={active ? "step" : undefined}>
                  {inner}
                </span>
              )}
              {i < steps.length - 1 ? (
                <span className="review-booking-stepper__sep" aria-hidden>
                  ·
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
